import { Header, Sidebar } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useImportSharedTemplate, useSharedTemplate } from '@/hooks/useSharedTemplate';
import {
  clearPendingSharedTemplateToken,
  readPendingSharedTemplateToken,
  subscribePendingSharedTemplateToken,
} from '@/lib/pendingSharedTemplate';
import { ApiError } from '@/lib/queryClient';
import { Loader2 } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { Link, useLocation } from 'wouter';

interface TemplateSharedBodyProps {
  token: string;
}

const ReadOnlyBlock = ({ label, text }: { label: string; text: string }) => (
  <section className="space-y-1">
    <h3 className="font-medium text-sm">{label}</h3>
    <pre className="whitespace-pre-wrap rounded-md border bg-muted/40 p-3 font-sans text-sm">{text}</pre>
  </section>
);

const InvalidLink = () => (
  <Card>
    <CardHeader>
      <CardTitle>Þessi tengill er ógildur</CardTitle>
      <CardDescription>Tengillinn er útrunninn eða sniðmátinu hefur verið eytt.</CardDescription>
    </CardHeader>
    <CardContent>
      <Button asChild variant="outline">
        <Link href="/templates">Fara í sniðmát</Link>
      </Button>
    </CardContent>
  </Card>
);

const TemplateSharedBody = ({ token }: TemplateSharedBodyProps) => {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { data, error, isLoading, isError, refetch, isFetching } = useSharedTemplate(token);
  const importMutation = useImportSharedTemplate();

  const handleImport = () =>
    importMutation.mutate(token, {
      onSuccess: () => {
        toast({ title: 'Sniðmát vistað' });
        // Navigate first so clearing the token does not flash the invalid state on the way out.
        setLocation('/templates');
        clearPendingSharedTemplateToken();
      },
      onError: () => {
        toast({ title: 'Villa', description: 'Ekki tókst að vista sniðmátið. Reyndu aftur.', variant: 'destructive' });
      },
    });

  if (isLoading) {
    return (
      <output className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Sæki sniðmátið…
      </output>
    );
  }

  if (isError && !(error instanceof ApiError && error.status === 404)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ekki tókst að sækja sniðmátið</CardTitle>
          <CardDescription>Eitthvað fór úrskeiðis. Reyndu aftur.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => void refetch()} disabled={isFetching}>
            Reyna aftur
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) return <InvalidLink />;

  const sender = [data.sender_name, data.sender_clinic].filter(Boolean).join(' · ');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{data.name}</CardTitle>
        {sender && <CardDescription>Sendandi: {sender}</CardDescription>}
        {data.description && <p className="text-muted-foreground text-sm">{data.description}</p>}
      </CardHeader>
      <CardContent className="space-y-4">
        <ReadOnlyBlock label="Sniðmát" text={data.template_content} />
        {data.custom_prompt && <ReadOnlyBlock label="Sérsniðnar leiðbeiningar" text={data.custom_prompt} />}
        {data.is_owner || data.already_imported ? (
          <div className="flex items-center gap-3">
            <span className="font-medium text-sm">
              {data.is_owner ? 'Þetta er þitt eigið sniðmát' : 'Sniðmátið er þegar vistað hjá þér'}
            </span>
            <Button asChild variant="outline">
              <Link href="/templates">Fara í sniðmát</Link>
            </Button>
          </div>
        ) : (
          <Button onClick={handleImport} disabled={importMutation.isPending}>
            {importMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Vista sniðmát
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const TemplateShared = () => {
  // Captured from the link's URL fragment (see main.tsx); a second link opened in this tab updates it.
  const token = useSyncExternalStore(subscribePendingSharedTemplateToken, readPendingSharedTemplateToken);

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header title="Deilt sniðmát" />
        <main className="flex-1 overflow-auto p-6">
          <div className="mx-auto max-w-3xl">
            {token ? <TemplateSharedBody key={token} token={token} /> : <InvalidLink />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TemplateShared;
